// client/src/components/profile/connectedApps/useConnectedApps.js
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config';
import { isAppKind } from './groupApps';

const opts = { withCredentials: true };
// The server looks up "Access given" dates in the background after a refresh, so
// the response can arrive without them. One quiet re-read after this delay picks
// them up (never repeated by itself, so it can't loop).
const DATES_RELOAD_MS = 8000;
const missingDates = (apps) => (apps || []).some((a) => a.still_granted && isAppKind(a) && !a.access_given_at && !a.access_given_before);

/**
 * Connected apps for the signed-in user (no userId) or a member (isMemberView).
 * Tick/undo is optimistic with rollback; disconnect is NOT optimistic because
 * it is an external write that can fail (spec §5).
 */
export default function useConnectedApps({ userId, isMemberView }) {
  const base = isMemberView
    ? `${API_BASE_URL}/api/profile/${userId}/connected-apps`
    : `${API_BASE_URL}/api/profile/connected-apps`;
  const [status, setStatus] = useState('loading');
  const [data, setData] = useState(null);
  // Only the latest load for the current `base` may write state, and nothing
  // writes after unmount: a slow GET for a previously viewed profile must not
  // overwrite the one now on screen.
  const seq = useRef(0);
  const baseRef = useRef(base);
  baseRef.current = base;
  const mounted = useRef(true);
  const datesTimer = useRef(null);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; clearTimeout(datesTimer.current); datesTimer.current = null; };
  }, []);
  // A different profile: its pending quiet re-read must not fire.
  useEffect(() => () => { clearTimeout(datesTimer.current); datesTimer.current = null; }, [base]);
  const live = (forBase) => mounted.current && baseRef.current === forBase;

  // silent: a background top-up must not flash the skeleton or replace shown
  // data with an error if it fails.
  const reload = useCallback(async ({ silent = false } = {}) => {
    const mine = ++seq.current;
    if (!silent) setStatus('loading');
    try {
      const r = await axios.get(base, opts);
      if (!live(base) || mine !== seq.current) return;
      setData(r.data);
      setStatus('ready');
    } catch {
      if (!live(base) || mine !== seq.current) return;
      if (!silent) setStatus('error');
    }
  }, [base]);

  useEffect(() => { reload(); }, [reload]);

  const replaceApp = (next) => {
    if (!mounted.current) return;
    setData((d) => (d ? { ...d, apps: d.apps.map((a) => (a.id === next.id ? next : a)) } : d));
  };

  const refresh = useCallback(async () => {
    // Newer data is coming: discard any in-flight older GET (e.g. the quiet
    // re-read) and cancel a pending one, so neither can overwrite this result.
    ++seq.current;
    clearTimeout(datesTimer.current);
    datesTimer.current = null;
    const r = await axios.post(`${base}/refresh`, {}, opts);
    if (live(base)) {
      setData(r.data);
      if (missingDates(r.data && r.data.apps) && !datesTimer.current) {
        datesTimer.current = setTimeout(() => {
          datesTimer.current = null;
          if (live(base)) reload({ silent: true });
        }, DATES_RELOAD_MS);
      }
    }
    return r.data;
  }, [base, reload]);

  const setClosed = useCallback(async (app, closed) => {
    replaceApp({ ...app, closed_at: closed ? new Date().toISOString() : null });
    try {
      const url = `${base}/${app.id}/closed`;
      const r = closed ? await axios.put(url, {}, opts) : await axios.delete(url, opts);
      replaceApp(r.data.app);
    } catch (err) {
      replaceApp(app);
      throw err;
    }
  }, [base]);

  const disconnect = useCallback(async (app) => {
    const r = await axios.post(`${API_BASE_URL}/api/profile/connected-apps/${app.id}/disconnect`, {}, opts);
    replaceApp(r.data.app);
  }, []);

  return { status, data, reload, refresh, setClosed, disconnect };
}
